/**
 * Locale resolution for the generated product index.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_products` stores every text field three times (`_zhHant` /
 * `_zhHans` / `_en`) and the public API collapses them per request
 * locale. The build-time sync used to fetch the index once, with
 * `?locale=zh-Hant`, so the generated `products.generated.ts` carried
 * Chinese copy only — `/en/products` showed Chinese product names even
 * though the database (and the API) had English ready.
 *
 * The sync now fetches all three locales and merges them into
 * `entry.locales`. This module is the front-end half of that: it picks
 * the right locale's copy out of a generated entry.
 *
 * THE TWO SHAPES
 * --------------
 *   `CmsProductIndexEntry`  — the raw generated row. Its flat `title` /
 *                              `description` / … fields mirror zh-Hant.
 *   `LocalizedProduct`      — the same row with the requested locale's
 *                              copy spread over the flat fields, so a
 *                              renderer can keep reading `product.title`
 *                              without caring about locales at all.
 *
 * The spread keeps the flat shape deliberately: the product cards are
 * shared with the flagship layout, the comparison table and the pricing
 * grid, and all three read the same field names.
 *
 * DEFENSIVE ABOUT A STALE GENERATED FILE
 * --------------------------------------
 * `ProductLocaleCopy` is declared here rather than imported from the
 * generated module, and `locales` is read through a cast. A generated
 * file written by an older sync (or by a watcher still running the old
 * code) has no `locales` key at all — the page then falls back to the
 * flat zh-Hant fields rather than failing to compile. The fallback is
 * a degraded but correct Chinese page, never a broken build.
 */

import { cmsProducts, type CmsProductIndexEntry } from './products.generated'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'

/** One locale's copy of a product's text fields. */
export interface ProductLocaleCopy {
  badge: string
  title: string
  description: string
  features: unknown[]
  audience: string
  table_features: string
  short_title: string
  image_alt: string
}

/** A generated row with one locale's copy resolved onto its flat fields. */
export type LocalizedProduct = Omit<CmsProductIndexEntry, 'locales'> & ProductLocaleCopy

/** The `locales` map, as the locale-aware sync writes it. */
type ProductLocales = Partial<Record<Locale, ProductLocaleCopy>>

/**
 * The copy for one locale of a generated row.
 *
 * Falls back to zh-Hant when the requested locale has no entry — the
 * generated file always carries all three, but a file written by an
 * older sync carries none, and the flat fields are the zh-Hant copy.
 */
export function productCopy(
  product: CmsProductIndexEntry,
  locale: Locale,
): ProductLocaleCopy {
  const locales = (product as { locales?: ProductLocales }).locales
  const copy = locales?.[locale] ?? locales?.[DEFAULT_LOCALE]
  if (copy) return copy

  // A generated file with no `locales` map: the flat fields are zh-Hant.
  return {
    badge: product.badge,
    title: product.title,
    description: product.description,
    features: product.features ?? [],
    audience: product.audience,
    table_features: product.table_features,
    short_title: product.short_title,
    image_alt: product.image_alt,
  }
}

/** One generated row, resolved for `locale`. */
export function localizeProduct(
  product: CmsProductIndexEntry,
  locale: Locale,
): LocalizedProduct {
  const { locales: _locales, ...rest } = product as CmsProductIndexEntry & {
    locales?: ProductLocales
  }
  return { ...rest, ...productCopy(product, locale) }
}

/** The whole generated index, resolved for `locale`. */
export function localizeProducts(
  products: CmsProductIndexEntry[],
  locale: Locale,
): LocalizedProduct[] {
  return products.map((p) => localizeProduct(p, locale))
}

/** The whole generated index, resolved for the active locale. */
export function localizedProducts(locale: Locale): LocalizedProduct[] {
  return localizeProducts(cmsProducts, locale)
}
